import styles from './Shifts.module.css';

import React from 'react';

import { DateTime } from 'luxon';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Typography from '@mui/material/Typography';

import Checkbox from '@mui/material/Checkbox';
import Check from '@mui/icons-material/Check';
import Construction from '@mui/icons-material/Construction';
import Groups from '@mui/icons-material/Groups';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Timer from '@mui/icons-material/Timer';
import TimerOff from '@mui/icons-material/TimerOff';

import { Shift } from './Shift';

export const ShiftsList = ({ shifts, roles, patrollers, readOnly, selectedShifts, allVisibleSelected, onCheckboxChange, onSelectAll, onNewShift, onSaveShift, onCancelEditShift, onCancelShift, onAssignPatrollers, shiftIntegrityIssues }) => {
    const [ expandedDates, setExpandedDates ] = React.useState(new Set([DateTime.now().toISODate()])); // Start with today expanded

    // Group shifts by date
    const shiftsByDate = shifts.reduce((acc, shift) => {
        const date = shift.date;

        if (!acc[date]) {
            acc[date] = [];
        }

        acc[date].push(shift);

        return acc;
    }, {});

    // Sort dates chronologically
    const sortedDates = Object.keys(shiftsByDate).sort((a, b) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis());
    
    // Check if today has shifts
    const today = DateTime.now().toISODate();
    const hasTodayShifts = shiftsByDate[today] && shiftsByDate[today].length > 0;

    // Update expanded dates when selectedShifts changes
    React.useEffect(() => {
        setExpandedDates(prev => {
            const newExpanded = new Set(prev);
            
            // Add dates for selected shifts
            selectedShifts.forEach(shift => {
                newExpanded.add(shift.date);
            });
            
            return newExpanded;
        });
    }, [selectedShifts]);

    // Handle accordion expansion changes
    const handleAccordionChange = (date) => (event, isExpanded) => {
        setExpandedDates(prev => {
            const newExpanded = new Set(prev);

            if (isExpanded) {
                newExpanded.add(date);
            } else {
                newExpanded.delete(date);
            }

            return newExpanded;
        });
    };

    const formattedTime = (time) => {
        if (!time) {
            return '';
        }

        return /^\d{2}:\d{2}(:\d{2})?$/.test(time)
            ? DateTime.fromFormat(time, "HH:mm").toFormat("HH:mm")
            : DateTime.fromMillis(time).toFormat("HH:mm")
    };

    const isValidTimeString = (timeString) => {
        // Validate timeString (HH:mm)
        return /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeString);
    };

    const isValidDateString = (dateString) => {
        // Validate dateString (YYYY-MM-DD)
        return /^\d{4}-\d{2}-\d{2}$/.test(dateString) && !isNaN(DateTime.fromISO(dateString).toMillis());
    };

    const convertTimeToEpoch = (timeString, dateString) => {
        if (!isValidTimeString(timeString) || !isValidDateString(dateString)) {
            return null;
        }

        const [ hours, minutes ] = timeString.split(':').map(Number);

        return DateTime.fromObject(
            {
                year: Number(dateString.slice(0, 4)),
                month: Number(dateString.slice(5, 7)),
                day: Number(dateString.slice(8, 10)),
                hour: hours,
                minute: minutes,
            },
            { zone: 'local' }
        ).toMillis();
    };

    const handleSelectAllChange = (event) => {
        const checked = event.target.checked;

        onSelectAll(checked);
    };

    const handleCheckboxChange = (shift, checked) => {
        onCheckboxChange(prev =>
            checked ? [ ...prev, shift ] : prev.filter(s => s.id !== shift.id)
        );
    };

    const handleEditShift = (shift) => {
        const startEpoch = convertTimeToEpoch(formattedTime(shift.start), shift.date);
        const endEpoch = convertTimeToEpoch(formattedTime(shift.end), shift.date);

        onSaveShift({
            ...shift,
            start: startEpoch !== null ? startEpoch : shift.start,
            end: endEpoch !== null ? endEpoch : shift.end,
        });
    };

    const handleCancelEditShift = () => {
        onCancelEditShift();

        onSaveShift(null);
    };

    const handleCancelShift = (shiftId, notify = false) => {
        onCancelShift(shiftId, notify);
    };

    return (
        <div className={ styles.shifts }>
            {/* Header Row */}
            <Grid container spacing={ 1 } sx={ { width: '100%', display: 'flex', mb: 2 } }>
                <Grid sx={ { p: 0, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ readOnly ? 2 : 1 } display={ readOnly ? 'none' : 'flex' } justifyContent='center' alignItems='center'>
                    <Checkbox
                        checked={ allVisibleSelected }
                        onChange={ handleSelectAllChange }
                        indeterminate={ selectedShifts.length > 0 && !allVisibleSelected }
                    />
                </Grid>

                <Grid sx={ { p: 1, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ readOnly ? 4 : 3 } display='flex' alignItems='center'>
                    <Typography variant="title">Name</Typography>
                </Grid>

                <Grid sx={ { p: 1, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ readOnly ? 4 : 3 } display='flex' alignItems='center'>
                    <Typography variant="title">Date</Typography>
                </Grid>

                <Grid sx={ { p: 0, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                    <Icon component={ Timer } />
                </Grid>

                <Grid sx={ { p: 0, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                    <Icon component={ TimerOff } />
                </Grid>

                <Grid sx={ { p: 0, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                    <Icon component={ SaveAlt } />
                </Grid>

                <Grid sx={ { p: 0, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ 1 } display='flex' justifyContent='center' alignItems='center'>
                    <Icon component={ Groups } />
                </Grid>

                <Grid sx={ { p: 0, backgroundColor: 'grey.700', borderRadius: '4px' } } size={ 1 } display={ readOnly ? 'none' : 'flex' } justifyContent='center' alignItems='center'>
                    <Icon component={ Construction } />
                </Grid>
            </Grid>

            {/* Accordions by Date */}
            { sortedDates.length > 0 ? (
                sortedDates.map(date => {
                    const dayShifts = shiftsByDate[date];
                    const dayName = DateTime.fromISO(date).toFormat('ccc, MMM d, yyyy');
                    const shiftCount = dayShifts.length;
                    const totalPositions = dayShifts.map(shift => shift.slots).flat().reduce((acc, slot) => acc + (slot.count || 0), 0) || 0;
                    const availablePositions = dayShifts.reduce((acc, shift) => acc + shift.available, 0);

                    return (
                        <Accordion 
                            key={ date } 
                            expanded={ expandedDates.has(date) } 
                            onChange={ handleAccordionChange(date) }
                            sx={{ width: '100%', mb: 1 }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: 'grey.800',
                                    '&:hover': { backgroundColor: 'grey.700' }
                                }}
                            >
                                
                                <Typography variant="body1" minWidth='175px'>
                                    { dayName }
                                </Typography>

                                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                                
                                <Typography variant="body1" minWidth='75px'>
                                    { shiftCount } shift{ shiftCount !== 1 ? 's' : '' }
                                </Typography>

                                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

                                <Typography variant="body1" color={ availablePositions === 0 ? 'success.main' : availablePositions < 0 ? 'error.main' : 'warning.main' }>
                                    { availablePositions } of { totalPositions } positions open
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                                { dayShifts.map(f => {
                                    const shiftIssues = shiftIntegrityIssues?.filter(issue => issue.shiftId === f.id) || [];
                                    
                                    return (
                                        <Shift 
                                            key={ f.id } 
                                            shift={ f } 
                                            patrollers={ patrollers }
                                            roles={ roles } 
                                            readOnly={ readOnly } 
                                            selectedShifts={ selectedShifts } 
                                            onCheckboxChange={ handleCheckboxChange } 
                                            onSaveShift={ handleEditShift } 
                                            onCancelEditShift={ handleCancelEditShift }
                                            onCancelShift={ handleCancelShift } 
                                            onAssignPatrollers={ onAssignPatrollers }
                                            integrityIssues={ shiftIssues } 
                                        />
                                    );
                                }) }
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            ) : (
                <div className={ styles.noShifts }>
                    <span className={ styles.warn } onClick={ onNewShift }>add shifts to assign roles</span>
                </div>
            ) }
        </div>
    );
};